<template>
  <div class="h-full w-full flex">
    <div v-if="!isAdmin" class="bg-gray-50 hidden lg_block lg_w-2/5">
      <img class="object-cover w-full h-full" :src="bgImage" />
    </div>

    <div
      :class="{
        'lg_w-3/5 pt-24 px-4 pb-4': !isAdmin,
      }"
      class="relative w-full flex flex-col items-center lg_justify-center"
    >
      <div class="w-full max-w-lg">
        <MeetingsFormEnterCode v-if="!meetingCode || isRedirecting" @submit="onSubmitMeetingCode" />
        <div v-if="shouldShowStartMeetingPrompt" class="w-full border rounded-lg px-4 lg_px-8 py-6 flex justify-between items-center mt-6">
          <span>Want to host an Online Meeting?</span>
          <Button variant="secondary" @click="onHostMeeting">Start Meeting</Button>
        </div>
        <MeetingsFormEnterName
          v-else-if="meetingCode && !userState.user && !showLoginForm && !isRedirecting"
          :meeting-code="meetingCode"
          :go-to-meeting="goToMeeting"
          @submit="onGuestSubmit"
          @toggle-login-form="onToggleLoginForm"
        />
        <MeetingsFormLogin
          v-else-if="showLoginForm && !isRedirecting"
          :meeting-code="meetingCode"
          @toggle-login-form="onToggleLoginForm"
          @submit="onSubmitLogin"
        />
      </div>
    </div>
  </div>

  <Teleport v-if="isAdmin" defer to="#admin-breadcrumbs">
    <BreadcrumbTrail :breadcrumbs="breadcrumbs" />
  </Teleport>
</template>

<script setup lang="ts">
import { FetchError } from 'ofetch'
import * as Sentry from '@sentry/vue'

import { useToast } from '~/components/ui/toast'
import { UserPermissionFlag } from '~~/entities/user'
import type { GuestUser } from '~~/entities/user'
import type { BreadcrumbData } from '~/components/ui/breadcrumb/BreadcrumbTrail.vue'
import bgImage from '~~/public/images/meetings-portrait-bg.jpg'
import type { MeetingRecord } from '~~/entities/meeting'

defineProps<{
  isAdmin?: boolean
}>()

const config = useRuntimeConfig()
const { $analytics } = useNuxtApp()
const { toast } = useToast()
const { state: userState, setGuestUserInfo, create, hasPermission } = useUser()
const { selectedCompany } = useCompanies()
const { createMeeting } = useMeetings()

const meetingCode = ref<string | undefined>()
const showLoginForm = ref(false)
const breadcrumbs = ref<BreadcrumbData[]>([{ text: 'Meetings' }])
const isRedirecting = ref(false)

const shouldShowStartMeetingPrompt = computed(() => {
  return (!meetingCode.value || isRedirecting.value) && canCreateMeeting.value && hasPermission(UserPermissionFlag.Presentations)
})
const managedCompanies = computed(() => userState.value.user?.companies ?? [])
const canCreateMeeting = computed(() => !userState.value.loading && userState.value.user && managedCompanies.value.length > 0)
// Handlers

const onToggleLoginForm = () => {
  showLoginForm.value = !showLoginForm.value
}

const onSubmitMeetingCode = async (code: string) => {
  if (!code) {
    return
  }

  const conferenceExists = await doesConferenceExist(code)
  if (!conferenceExists) {
    return
  }

  $analytics.track('MeetingsPageClickJoin')

  if (userState.value.user) {
    isRedirecting.value = true
    meetingCode.value = code
    goToMeeting()
  } else {
    meetingCode.value = code
  }
}

const onGuestSubmit = (guestName: string) => {
  $analytics.track('guestConnectJoinSubmitted')

  const guestUserInfo: GuestUser = {
    name: guestName.trim(),
    shareCode: meetingCode.value,
  }

  setGuestUserInfo(guestUserInfo)

  const userProperties = {
    id: null,
    name: guestUserInfo?.name || '',
    emailAddress: null,
    profileImage: null,
  }

  $analytics.identify(userProperties)

  goToMeeting()
}

const onSubmitLogin = async (values: { email: string; password: string }) => {
  try {
    await create({ email_address: values.email ?? '', password: values.password ?? '' })
    goToMeeting()
  } catch (e) {
    if (e instanceof FetchError) {
      $analytics.track('loginError', {
        errorMessage: e?.data.message,
      })
      toast({
        title: 'Login unsuccessful. Please check your credentials and try again.',
        variant: 'destructive',
      })
    }
  }
}

const onHostMeeting = async (options: Partial<MeetingRecord>) => {
  try {
    if (!selectedCompany.value) throw new Error('No selected company')

    $analytics.track('meetingsStartMeetingNowSelected', {
      companyId: selectedCompany.value?.id,
      companyName: selectedCompany.value?.name,
    })

    const { share_id } = await createMeeting({
      companyId: selectedCompany.value.id as number,
      title: 'Instant Meeting',
      ...options,
    })

    meetingCode.value = share_id
    isRedirecting.value = true

    goToMeeting()
  } catch (e) {
    Sentry.captureException(e)
    throw e
  }
}

// Methods

const doesConferenceExist = async (shareCode: string) => {
  try {
    await $fetch(`v2/conferences/${shareCode}`, useFetchSharedOptions())

    return true
  } catch (e) {
    $analytics.track('connectApiError', { shareCode })

    toast({
      title: 'Conference does not exist',
      description: `A VRIFY Connect session with code ${shareCode} does not exist. Please check the code and try again`,
      variant: 'destructive',
    })

    return false
  }
}

const goToMeeting = () => {
  const { selectedCompany } = useCompanies()
  let url = `${config.public.WEB_ENDPOINT}meeting/${meetingCode.value}`
  if (selectedCompany.value?.id) {
    url += `?companyId=${selectedCompany.value?.id}`
  }
  window.location.href = url
}

// Lifecycle

onMounted(() => {
  const route = useRoute()
  const { code } = route.query
  if (code) {
    onSubmitMeetingCode(code as string)
  }
})
</script>
